import type { TodaySignal } from "@/lib/dashboard/today-signal-types";
import {
  buildDefaultRoleSignalConfig,
  buildDefaultStudioSignalsSettings,
  NON_CONFIGURABLE_SIGNAL_IDS,
  SIGNAL_CATALOG_BY_ROLE,
  SIGNALS_ADMIN_CONFIG_KEY,
  SIGNALS_MEMBER_CONFIG_KEY,
  SIGNALS_STAFF_CONFIG_KEY,
  SIGNALS_TEACHER_CONFIG_KEY,
  STAFF_FORBIDDEN_SIGNAL_IDS,
  type RoleSignalConfigItem,
  type StudioSignalsSettings,
  type TodaySignalRole,
} from "./signals-settings-catalog";

export type {
  RoleSignalConfigItem,
  SignalCatalogEntry,
  StudioSignalsSettings,
  TodaySignalRole,
} from "./signals-settings-catalog";

export {
  buildDefaultRoleSignalConfig,
  buildDefaultStudioSignalsSettings,
  getSignalCatalogEntry,
  NON_CONFIGURABLE_SIGNAL_IDS,
  SIGNAL_CATALOG_BY_ROLE,
  SIGNALS_ADMIN_CONFIG_KEY,
  SIGNALS_MEMBER_CONFIG_KEY,
  SIGNALS_ROLE_LABEL,
  SIGNALS_SETTINGS_GROUP,
  SIGNALS_STAFF_CONFIG_KEY,
  SIGNALS_TEACHER_CONFIG_KEY,
  STAFF_FORBIDDEN_SIGNAL_IDS,
} from "./signals-settings-catalog";

export const DEFAULT_STUDIO_SIGNALS_SETTINGS = buildDefaultStudioSignalsSettings();

export type StudioSignalsSettingsPersisted = Record<TodaySignalRole, boolean>;

export const DEFAULT_STUDIO_SIGNALS_SETTINGS_PERSISTED: StudioSignalsSettingsPersisted =
  {
    admin: false,
    staff: false,
    teacher: false,
    member: false,
  };

const ALL_CLEAR_FALLBACK: TodaySignal = {
  id: "all-clear",
  priority: 999,
  severity: "calm",
  text: "All clear. No pending actions right now.",
  link: null,
  linkText: null,
};

export type ApplyTodaySignalSettingsOptions = {
  /** Signal ids disabled because a studio module is turned off. */
  moduleDisabledSignalIds?: readonly string[];
  /** Enforce staff permission floor even if settings enable forbidden ids. */
  enforceStaffPermissions?: boolean;
};

const MODULE_SIGNAL_MAP = {
  makeups: [
    "pending-makeup-requests",
    "makeup-eligible",
    "makeup-change-requested",
  ],
  appointments: [
    "private-lessons-today",
    "private-lessons-upcoming",
    "private-lessons-only",
  ],
  timeClock: ["time-clock-pending"],
} as const;

export type StudioFeatureModuleKey = keyof typeof MODULE_SIGNAL_MAP;

export const STUDIO_FEATURE_MODULE_LABEL: Record<StudioFeatureModuleKey, string> =
  {
    makeups: "Makeup requests",
    appointments: "Private lessons",
    timeClock: "Time clock",
  };

const SIGNAL_TO_FEATURE_MODULE = Object.entries(MODULE_SIGNAL_MAP).reduce(
  (acc, [moduleKey, signalIds]) => {
    for (const signalId of signalIds) {
      acc[signalId] = moduleKey as StudioFeatureModuleKey;
    }
    return acc;
  },
  {} as Record<string, StudioFeatureModuleKey>,
);

export type StudioFeatureModuleFlags = {
  makeupsEnabled?: boolean;
  appointmentsEnabled?: boolean;
  timeClockEnabled?: boolean;
};

function isFeatureModuleEnabled(
  moduleKey: StudioFeatureModuleKey,
  features: StudioFeatureModuleFlags,
): boolean {
  switch (moduleKey) {
    case "makeups":
      return features.makeupsEnabled !== false;
    case "appointments":
      return features.appointmentsEnabled !== false;
    case "timeClock":
      return features.timeClockEnabled !== false;
    default:
      return true;
  }
}

export function getSignalFeatureModule(
  signalId: string,
): StudioFeatureModuleKey | null {
  return SIGNAL_TO_FEATURE_MODULE[signalId] ?? null;
}

export function getSignalModuleLockReason(
  signalId: string,
  features: StudioFeatureModuleFlags,
): string | null {
  const moduleKey = getSignalFeatureModule(signalId);
  if (!moduleKey || isFeatureModuleEnabled(moduleKey, features)) {
    return null;
  }

  return `${STUDIO_FEATURE_MODULE_LABEL[moduleKey]} is disabled in Studio features. Enable it there to use this signal.`;
}

export function getModuleDisabledSignalIds(
  options: StudioFeatureModuleFlags,
): string[] {
  const disabled: string[] = [];
  if (options.makeupsEnabled === false) {
    disabled.push(...MODULE_SIGNAL_MAP.makeups);
  }
  if (options.appointmentsEnabled === false) {
    disabled.push(...MODULE_SIGNAL_MAP.appointments);
  }
  if (options.timeClockEnabled === false) {
    disabled.push(...MODULE_SIGNAL_MAP.timeClock);
  }
  return disabled;
}

function parseRoleConfigJson(
  raw: string | null | undefined,
  role: TodaySignalRole,
): RoleSignalConfigItem[] | null {
  if (raw == null || String(raw).trim() === "") return null;

  try {
    const parsed = JSON.parse(String(raw)) as unknown;
    if (!Array.isArray(parsed)) return null;

    const catalogIds = new Set(
      SIGNAL_CATALOG_BY_ROLE[role].map((entry) => entry.id),
    );
    const items: RoleSignalConfigItem[] = [];

    for (const item of parsed) {
      if (item == null || typeof item !== "object") continue;
      const id = "id" in item ? String((item as { id: unknown }).id) : "";
      if (!catalogIds.has(id)) continue;
      const enabled =
        "enabled" in item
          ? Boolean((item as { enabled: unknown }).enabled)
          : true;
      items.push({ id, enabled });
    }

    return items.length > 0 ? items : null;
  } catch {
    return null;
  }
}

export function normalizeRoleSignalConfig(
  role: TodaySignalRole,
  source: RoleSignalConfigItem[] | null | undefined,
): RoleSignalConfigItem[] {
  const defaults = buildDefaultRoleSignalConfig(role);
  if (!source || source.length === 0) {
    return defaults;
  }

  const catalogOrder = SIGNAL_CATALOG_BY_ROLE[role].map((entry) => entry.id);
  const sourceById = new Map(source.map((item) => [item.id, item]));
  const ordered: RoleSignalConfigItem[] = [];

  for (const id of source.map((item) => item.id)) {
    if (!catalogOrder.includes(id) || ordered.some((item) => item.id === id)) {
      continue;
    }
    const item = sourceById.get(id);
    if (item) ordered.push({ id, enabled: item.enabled });
  }

  for (const id of catalogOrder) {
    if (ordered.some((item) => item.id === id)) continue;
    const fallback = defaults.find((item) => item.id === id);
    if (fallback) ordered.push(fallback);
  }

  return ordered;
}

export function normalizeStudioSignalsSettings(
  source: Partial<Record<TodaySignalRole, RoleSignalConfigItem[]>> | null | undefined,
): StudioSignalsSettings {
  const roles: TodaySignalRole[] = ["admin", "staff", "teacher", "member"];
  return roles.reduce((acc, role) => {
    acc[role] = normalizeRoleSignalConfig(role, source?.[role]);
    return acc;
  }, {} as StudioSignalsSettings);
}

export function parseStudioSignalsSettingsFromMap(
  map: Map<string, string>,
): {
  settings: StudioSignalsSettings;
  persisted: StudioSignalsSettingsPersisted;
} {
  const roleKeys: Record<TodaySignalRole, string> = {
    admin: SIGNALS_ADMIN_CONFIG_KEY,
    staff: SIGNALS_STAFF_CONFIG_KEY,
    teacher: SIGNALS_TEACHER_CONFIG_KEY,
    member: SIGNALS_MEMBER_CONFIG_KEY,
  };

  const parsed: Partial<Record<TodaySignalRole, RoleSignalConfigItem[]>> = {};
  const persisted: StudioSignalsSettingsPersisted = {
    ...DEFAULT_STUDIO_SIGNALS_SETTINGS_PERSISTED,
  };

  (Object.keys(roleKeys) as TodaySignalRole[]).forEach((role) => {
    const key = roleKeys[role];
    const raw = map.get(key);
    const config = parseRoleConfigJson(raw, role);
    if (config) {
      parsed[role] = config;
      persisted[role] = true;
    }
  });

  return {
    settings: normalizeStudioSignalsSettings(parsed),
    persisted,
  };
}

export function serializeRoleSignalConfig(
  config: RoleSignalConfigItem[],
): string {
  return JSON.stringify(
    config.map((item) => ({ id: item.id, enabled: item.enabled })),
  );
}

export function roleSignalConfigsEqual(
  a: RoleSignalConfigItem[],
  b: RoleSignalConfigItem[],
): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (item, index) =>
      item.id === b[index]?.id && item.enabled === b[index]?.enabled,
  );
}

export function studioSignalsSettingsEqual(
  a: StudioSignalsSettings,
  b: StudioSignalsSettings,
): boolean {
  return (
    roleSignalConfigsEqual(a.admin, b.admin) &&
    roleSignalConfigsEqual(a.staff, b.staff) &&
    roleSignalConfigsEqual(a.teacher, b.teacher) &&
    roleSignalConfigsEqual(a.member, b.member)
  );
}

function isSignalAllowed(
  signalId: string,
  configById: Map<string, RoleSignalConfigItem>,
  options?: ApplyTodaySignalSettingsOptions,
): boolean {
  const configItem = configById.get(signalId);
  if (configItem && !configItem.enabled) return false;
  if (configItem == null && configById.size > 0) return false;

  if (options?.moduleDisabledSignalIds?.includes(signalId)) return false;

  if (
    options?.enforceStaffPermissions &&
    STAFF_FORBIDDEN_SIGNAL_IDS.includes(
      signalId as (typeof STAFF_FORBIDDEN_SIGNAL_IDS)[number],
    )
  ) {
    return false;
  }

  return true;
}

/**
 * Filters and re-orders built dashboard signals using studio settings.
 * Non-configurable signals (`welcome`, `all-clear`) pass through unchanged.
 */
export function applyTodaySignalSettings(
  signals: TodaySignal[],
  roleConfig: RoleSignalConfigItem[],
  options?: ApplyTodaySignalSettingsOptions,
): TodaySignal[] {
  const nonConfigurable = signals.filter((signal) =>
    NON_CONFIGURABLE_SIGNAL_IDS.has(signal.id),
  );

  if (nonConfigurable.some((signal) => signal.id === "welcome")) {
    return nonConfigurable;
  }

  const priorityById = new Map(
    roleConfig.map((item, index) => [item.id, (index + 1) * 10]),
  );
  const configById = new Map(roleConfig.map((item) => [item.id, item]));

  const configurable = signals
    .filter((signal) => !NON_CONFIGURABLE_SIGNAL_IDS.has(signal.id))
    .filter((signal) => isSignalAllowed(signal.id, configById, options))
    .map((signal) => {
      const overridePriority = priorityById.get(signal.id);
      return overridePriority != null
        ? { ...signal, priority: overridePriority }
        : signal;
    })
    .sort((a, b) => a.priority - b.priority);

  if (configurable.length === 0) {
    const existingAllClear = signals.find((signal) => signal.id === "all-clear");
    return [existingAllClear ?? ALL_CLEAR_FALLBACK];
  }

  return configurable;
}
